import React, { useState, useEffect, useCallback } from "react";
import {
    TrendingUp, TrendingDown, DollarSign, RefreshCw,
    BarChart3, PieChart, ArrowRight, Users, Download,
} from "lucide-react";
import api from "../../utils/api";

// ─── formatters ──────────────────────────────────────────────────────────────

function fmtUSD(n) {
    if (n == null) return "—";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${n.toLocaleString()}`;
}

function fmtDelta(n) {
    if (n == null) return null;
    return n >= 0 ? `+${n}%` : `${n}%`;
}

// ─── plan colours ─────────────────────────────────────────────────────────────
const PLAN_COLORS = {
    professional: "bg-navy-900",
    growth: "bg-violet-600",
    starter: "bg-blue-400",
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ title, value, delta, isPositive, sub }) {
    const deltaEl = delta
        ? <span className={`flex items-center gap-1 text-xs font-bold ${isPositive ? "text-emerald-600" : "text-red-500"}`}>
            {isPositive ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {delta}
            <span className="text-slate-400 font-normal ml-1">{sub || "vs last month"}</span>
          </span>
        : <span className="text-xs text-slate-400">{sub || "—"}</span>;

    return (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 bg-slate-50 rounded-bl-full -mr-4 -mt-4 transition-all group-hover:scale-110" />
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-4">{title}</h3>
            <div className="text-3xl font-bold text-navy-900 font-serif mb-2">{value ?? "—"}</div>
            {deltaEl}
        </div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function RevenueAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const res = await api.get("/api/payments/revenue-summary");
            setData(res.data);
            setError(null);
            setLastFetched(new Date());
        } catch {
            setError("Could not load revenue data");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    // Export CSV
    const handleExport = () => {
        if (!data) return;
        const rows = [
            ["Metric", "Value"],
            ["MRR", data.mrr],
            ["ARR", data.arr],
            ["ARPA", data.arpa],
            ["Tenants", data.tenantCount],
            ["Churn Rate (30d)", `${data.churnRate}%`],
            ["Square Status", data.square?.status],
            ["Square Collected (12mo)", data.square?.totalCollected ?? 0],
            [],
            ["Plan", "Tenants", "MRR", "%"],
            ...(data.planBreakdown || []).map(p => [p.label, p.count, p.mrr, `${p.pct}%`]),
        ];
        const csv = rows.map(r => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tinyglobal-revenue-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Chart: pick Square payment totals if available, else invoice totals, else 0
    const chartData = data?.monthlyHistory || [];
    const maxBar = Math.max(...chartData.map(m => m.total), 1);

    // Optimization tip based on real plan distribution
    const starterCount = data?.planBreakdown?.find(p => p.plan === "starter")?.count ?? 0;
    const totalTenants = data?.tenantCount ?? 0;
    const optimizationNote = starterCount > 0
        ? `${starterCount} of ${totalTenants} tenant${totalTenants !== 1 ? "s" : ""} on Starter — upgrade to Growth unlocks $${(starterCount * 150).toLocaleString()}/mo additional MRR.`
        : totalTenants > 0
        ? "All tenants on paid plans. Focus on new tenant acquisition."
        : "No tenants yet — start onboarding your first SME clients.";

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto py-8 flex items-center justify-center h-64 gap-3 text-slate-400">
                <div className="w-6 h-6 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
                <span className="text-sm">Loading revenue data…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto py-8 flex items-center justify-center h-64 text-red-400 text-sm">
                {error} — <button onClick={() => load(false)} className="ml-2 text-blue-500 underline">retry</button>
            </div>
        );
    }

    const mrrDelta = fmtDelta(data?.mrrGrowth);
    const arrDelta = data?.mrrGrowth != null ? fmtDelta(Math.round(data.mrrGrowth * 1.1)) : null;

    return (
        <div className="max-w-7xl mx-auto py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">Revenue Analytics</h1>
                    <p className="text-slate-500 mt-1">
                        Live data from Firestore &amp; Square
                        {data?.square?.status === "connected" && (
                            <span className="ml-2 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                                Square {data.square.env}
                            </span>
                        )}
                        {lastFetched && (
                            <span className="ml-2 text-[11px] text-slate-400">
                                · updated {lastFetched.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => load(true)}
                        disabled={refreshing}
                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-navy-900 transition-all disabled:opacity-40"
                        title="Refresh"
                    >
                        <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
                    </button>
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 bg-navy-900 text-white rounded-xl text-xs font-bold hover:bg-navy-800 transition-colors"
                    >
                        <Download size={14} /> Export CSV
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <KpiCard
                    title="Monthly Recurring Revenue"
                    value={fmtUSD(data?.mrr)}
                    delta={mrrDelta}
                    isPositive={(data?.mrrGrowth ?? 0) >= 0}
                />
                <KpiCard
                    title="Average Revenue Per Account"
                    value={data?.arpa ? `$${data.arpa.toLocaleString()}` : "—"}
                    delta={null}
                    sub={`${data?.tenantCount ?? 0} active tenant${(data?.tenantCount ?? 0) !== 1 ? "s" : ""}`}
                />
                <KpiCard
                    title="Churn Rate (30d)"
                    value={data?.churnRate != null ? `${data.churnRate}%` : "—"}
                    delta={null}
                    isPositive={true}
                    sub={data?.churnRate === 0 ? "No churn this period" : "churned tenants"}
                />
                <KpiCard
                    title="Projected Annual Revenue"
                    value={fmtUSD(data?.arr)}
                    delta={arrDelta}
                    isPositive={(data?.mrrGrowth ?? 0) >= 0}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* MRR / Invoice History Chart */}
                <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-navy-900 flex items-center gap-2">
                            <BarChart3 size={20} className="text-blue-500" /> MRR History (12 months)
                        </h3>
                        <div className={`text-xs font-bold px-3 py-1 rounded-full ${
                            data?.square?.status === "connected"
                                ? "text-emerald-600 bg-emerald-50"
                                : "text-slate-500 bg-slate-100"
                        }`}>
                            {data?.square?.status === "connected" ? "Square live" : "Firestore invoices"}
                        </div>
                    </div>

                    {chartData.every(m => m.total === 0) ? (
                        <div className="h-48 flex items-center justify-center text-slate-300 text-sm text-center">
                            No invoice or payment history yet.<br />Chart will populate as payments are recorded.
                        </div>
                    ) : (
                        <div className="h-48 flex items-end gap-1.5">
                            {chartData.map((m, i) => {
                                const pct = m.total > 0 ? Math.max(6, Math.round((m.total / maxBar) * 100)) : 4;
                                return (
                                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                        <div className="w-full flex items-end" style={{ height: "180px" }}>
                                            <div
                                                className="w-full bg-navy-900/10 hover:bg-navy-900 transition-all rounded-t-md relative group"
                                                style={{ height: `${pct}%` }}
                                            >
                                                {m.total > 0 && (
                                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-navy-900 text-white text-[9px] font-bold px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                                        {fmtUSD(m.total)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">{m.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-6 grid grid-cols-3 gap-4 pt-6 border-t border-slate-50 text-center">
                        <div>
                            <div className="text-lg font-bold text-navy-900">{fmtUSD(data?.arr)}</div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase">Total ARR</div>
                        </div>
                        <div>
                            <div className="text-lg font-bold text-navy-900">
                                {data?.square?.status === "connected"
                                    ? fmtUSD(data.square.totalCollected)
                                    : "—"}
                            </div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase">Square Collected</div>
                        </div>
                        <div>
                            <div className="text-lg font-bold text-navy-900">{data?.tenantCount ?? "—"}</div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase">Active Tenants</div>
                        </div>
                    </div>
                </div>

                {/* Revenue by Plan */}
                <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-navy-900 flex items-center gap-2 mb-6">
                        <PieChart size={20} className="text-violet-500" /> Revenue by Plan Type
                    </h3>

                    {(data?.planBreakdown?.every(p => p.count === 0)) ? (
                        <div className="h-32 flex items-center justify-center text-slate-300 text-sm">
                            No tenants assigned to plans yet.
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {(data?.planBreakdown || []).map((plan) => (
                                <div key={plan.plan} className="space-y-2">
                                    <div className="flex justify-between items-center text-sm">
                                        <div>
                                            <span className="font-bold text-navy-900">{plan.label}</span>
                                            <span className="ml-2 text-[10px] text-slate-400">{plan.count} tenant{plan.count !== 1 ? "s" : ""} · ${plan.price}/mo</span>
                                        </div>
                                        <span className="font-mono text-slate-600 text-sm">
                                            {fmtUSD(plan.mrr)} <span className="text-slate-400">({plan.pct}%)</span>
                                        </span>
                                    </div>
                                    <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-700 ${PLAN_COLORS[plan.plan] || "bg-slate-400"}`}
                                            style={{ width: `${plan.pct || 0}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Optimization insight */}
                    <div className="mt-8 p-5 bg-slate-50 rounded-2xl border border-slate-100">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h4 className="font-bold text-navy-900 text-sm flex items-center gap-2">
                                    <Users size={14} className="text-violet-500" /> Optimization Opportunity
                                </h4>
                                <p className="text-xs text-slate-500 mt-1">{optimizationNote}</p>
                            </div>
                            <button className="p-2 bg-white border border-slate-200 rounded-xl text-navy-900 hover:shadow-md transition-all shrink-0">
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Square summary if connected */}
                    {data?.square?.status === "connected" && (
                        <div className="mt-4 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 pt-4">
                            <span>Square {data.square.env} · {data.square.count} payment{data.square.count !== 1 ? "s" : ""} collected</span>
                            <span className="font-bold text-emerald-600">{fmtUSD(data.square.totalCollected)} collected (12mo)</span>
                        </div>
                    )}
                    {data?.square?.status === "unconfigured" && (
                        <p className="mt-4 text-[11px] text-amber-600 border-t border-slate-100 pt-4">
                            Square not configured — add SQUARE_ACCESS_TOKEN to Render env vars to see collected payment totals.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
